<template>
    <div>
        <card-header title="Support Settings" icon="fa-cog"/>

        <card-toolbar>
            <button @click="$openCard('support-canned-response', {responseId: 'new'}, card)"><i class="far fa-plus"></i>New Canned Response</button>
        </card-toolbar>

        <card-body toolbar>
            <card-list>
                <subheader-list-item title="Channels" icon="fa-envelope"/>

                <div v-for="channel in channels" :key="channel.id" class="channel-item">
                    <div class="channel-header" @click="toggleChannel(channel.id)">
                        <i class="far fa-fw mr-1" :class="channel.channel_type === 'sms' ? 'fa-comment' : (channel.channel_type === 'direct' ? 'fa-comments' : 'fa-envelope')"></i>
                        <strong>{{channel.name}}</strong>
                        <span class="has-text-grey is-size-7 ml-2">{{channel.email_address}}</span>
                        <i class="far ml-auto" :class="expandedChannelId === channel.id ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    </div>
                    <div v-if="expandedChannelId === channel.id" class="channel-detail">
                        <label class="label is-size-7">Signature</label>
                        <quill-editor v-model="channel._signatureHtml" :options="signatureEditorOptions" class="has-background-white content signature-editor"></quill-editor>
                        <div class="mt-2">
                            <b-button type="is-primary" size="is-small" @click="saveSignature(channel)" :loading="channel._saving">Save Signature</b-button>
                            <b-button size="is-small" @click="clearSignature(channel)">Clear</b-button>
                        </div>
                    </div>
                </div>

                <container-list-item padded v-if="channels.length === 0" class="has-text-grey is-size-7">
                    No channels found.
                </container-list-item>

                <separator-list-item/>

                <subheader-list-item title="Canned Responses" icon="fa-comment-alt-lines" :badge="responses.length"/>

                <card-list-item v-for="response in responses" :key="response.id"
                    :class="{'active': isActive(response.id)}"
                    @click="$openCard('support-canned-response', {responseId: response.id}, card)">
                    <template v-slot:icon><i class="far fa-comment-alt fa-fw"></i></template>
                    <template v-slot:content>
                        <div class="tile-title">{{response.title}}</div>
                        <div class="meta" v-if="response.category"><i class="fas fa-tag fa-fw mr-1"></i>{{response.category}}</div>
                    </template>
                    <template v-slot:status>
                        <div class="meta" v-if="response.domain_name"><i class="fas fa-globe fa-fw mr-1"></i>{{response.domain_name}}</div>
                        <div class="meta has-text-grey" v-else>All domains</div>
                    </template>
                </card-list-item>

                <container-list-item padded v-if="responses.length === 0" class="has-text-grey is-size-7">
                    No canned responses yet.
                </container-list-item>
            </card-list>
        </card-body>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardList from "@/TIER/components/CardList";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import CardListItem from "@/TIER/components/CardListItem";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";

    import SeparatorListItem from "@/TIER/components/SeparatorListItem";
    import {client as http} from '@/http_client';

    export default {
        components: {ContainerListItem, CardHeader, CardBody, CardList, CardToolbar, CardListItem, SubheaderListItem, SeparatorListItem},
        props: ['card', 'child', 'props'],
        data() {
            return {
                responses: [],
                channels: [],
                expandedChannelId: null,
                signatureEditorOptions: {
                    modules: {
                        toolbar: [
                            ['bold', 'italic', 'underline'],
                            [{'color': []}, {'background': []}],
                            [{'list': 'ordered'}, {'list': 'bullet'}],
                            ['link', 'image'],
                            ['clean']
                        ],
                        keyboard: {
                            bindings: {
                                linebreak: {
                                    key: 13,
                                    shiftKey: true,
                                    handler: function(range) {
                                        this.quill.insertEmbed(range.index, 'break', true, 'user');
                                        this.quill.setSelection(range.index + 1, 'silent');
                                        return false;
                                    }
                                }
                            }
                        }
                    },
                    placeholder: 'Email signature...'
                }
            }
        },
        methods: {
            loadChannels() {
                http.get('support/channels').then(response => {
                    this.channels = (response.data || []).filter(c => c.channel_type === 'email').map(c => ({
                        ...c,
                        _signatureHtml: c.signature_html || '',
                        _saving: false
                    }));
                }).catch(() => {});
            },
            toggleChannel(id) {
                this.expandedChannelId = this.expandedChannelId === id ? null : id;
            },
            saveSignature(channel) {
                this.$set(channel, '_saving', true);
                http.put('support/channels/' + channel.id, {
                    signature_html: channel._signatureHtml || null
                }).then(() => {
                    channel.signature_html = channel._signatureHtml;
                    this.$set(channel, '_saving', false);
                }).catch(err => {
                    console.log(err);
                    this.$set(channel, '_saving', false);
                });
            },
            clearSignature(channel) {
                channel._signatureHtml = '';
            },
            loadResponses() {
                http.get('support/canned-responses').then(response => {
                    this.responses = response.data || [];
                    this.$emit('loading', false);
                }).catch(err => {
                    console.log(err);
                    this.$emit('loading', false);
                });
            },
            isActive(id) {
                return this.child && this.child.definition.component === 'support-canned-response' && parseInt(this.child.props.responseId) === id;
            },
            reload() {
                this.loadResponses();
                this.loadChannels();
            }
        },
        mounted() {
            this.loadResponses();
            this.loadChannels();
        }
    }
</script>

<style lang="scss" scoped>
    .tile-title {
        font-size: 0.9rem;
        font-weight: bold;
    }
    .channel-item {
        border-bottom: 1px solid #f0f0f0;
    }
    .channel-header {
        display: flex;
        align-items: center;
        padding: 0.6rem 1rem;
        cursor: pointer;
        font-size: 0.85rem;
        &:hover {
            background: #fafafa;
        }
    }
    .channel-detail {
        padding: 0 1rem 0.75rem;
    }
    .signature-editor {
        min-height: 80px;
    }
</style>
